import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";
import { api, errMsg, money } from "@/lib/api";
import { ADMIN_EXTRA as T } from "@/constants/testIds";

const input = "px-3 py-2 rounded-xl border border-[#E7E0D3] bg-white text-sm";
const blank = { slug: "new", name: "", tagline: "", items: ["", ""], discount: 0.1, active: true };

const ItemPicker = ({ id, i, value, products, onChange, onRemove }) => (
  <div className="flex gap-2">
    <select data-testid={T.bundleItem(id, i)} value={value} onChange={(e) => onChange(e.target.value)} className={`${input} flex-1 min-w-0`}>
      <option value="">Pick a product…</option>
      {products.map((p) => <option key={p.slug} value={p.slug}>{p.name}{p.active ? "" : " (hidden)"} · {money(p.price)}</option>)}
    </select>
    {onRemove && <button type="button" aria-label="Remove product" onClick={onRemove} className="px-2 text-stone-400 hover:text-[#E11D48]"><X size={16} /></button>}
  </div>
);

const BundleTotals = ({ b, products }) => {
  const picked = b.items.map((s) => products.find((p) => p.slug === s)).filter(Boolean);
  const full = picked.reduce((n, p) => n + p.price, 0);
  const cost = picked.every((p) => p.cost != null) ? picked.reduce((n, p) => n + p.cost + (p.ship_cost || 0), 0) : null;
  const price = full * (1 - b.discount);
  return (
    <p className="text-xs text-stone-500">
      {money(full)} → <strong className="text-[#1C1917]">{money(price)}</strong>
      {cost != null && price > 0 && <> · margin {(((price - cost) / price) * 100).toFixed(0)}%</>}
    </p>
  );
};

const BundleForm = ({ initial, products, onSaved, onDeleted }) => {
  const [b, setB] = useState(initial);
  const id = initial.slug;
  const set = (patch) => setB((x) => ({ ...x, ...patch }));
  const setItem = (i, v) => set({ items: b.items.map((s, j) => (j === i ? v : s)) });
  const save = async (e) => {
    e.preventDefault();
    const body = { name: b.name, tagline: b.tagline, items: b.items.filter(Boolean), discount: b.discount, active: b.active };
    try {
      const { data } = id === "new" ? await api.post("/admin/bundles", body) : await api.put(`/admin/bundles/${id}`, body);
      onSaved(data, id);
      if (id === "new") setB(blank);
      toast.success("Bundle saved");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${b.name}?`)) return;
    try { await api.delete(`/admin/bundles/${id}`); onDeleted(id); toast.success("Bundle deleted"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form data-testid={T.bundleRow(id)} onSubmit={save} className="bg-white border border-[#F0EBE1] rounded-2xl p-4 grid md:grid-cols-2 gap-3">
      <div className="space-y-2">
        <input data-testid={T.bundleName(id)} required minLength={2} maxLength={80} value={b.name} onChange={(e) => set({ name: e.target.value })} placeholder="Bundle name" className={`${input} w-full font-semibold`} />
        <input data-testid={T.bundleTagline(id)} maxLength={160} value={b.tagline} onChange={(e) => set({ tagline: e.target.value })} placeholder="One-line pitch" className={`${input} w-full`} />
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2">Discount
            <input data-testid={T.bundleDiscount(id)} type="number" min="5" max="30" step="1" value={Math.round(b.discount * 100)} onChange={(e) => set({ discount: Number(e.target.value) / 100 })} className={`${input} w-20`} />%
          </label>
          <label className="flex items-center gap-2"><input data-testid={T.bundleActive(id)} type="checkbox" checked={b.active} onChange={(e) => set({ active: e.target.checked })} /> Show in shop</label>
        </div>
        <BundleTotals b={b} products={products} />
      </div>
      <div className="space-y-2">
        {b.items.map((s, i) => <ItemPicker key={i} id={id} i={i} value={s} products={products} onChange={(v) => setItem(i, v)} onRemove={b.items.length > 2 ? () => set({ items: b.items.filter((_, j) => j !== i) }) : null} />)}
        {b.items.length < 4 && <button type="button" data-testid={T.bundleAddItem(id)} onClick={() => set({ items: [...b.items, ""] })} className="text-xs inline-flex items-center gap-1 text-[#E06D44]"><Plus size={14} /> Add a product</button>}
        <div className="flex gap-2 justify-end pt-1">
          {id !== "new" && <button type="button" data-testid={T.bundleDelete(id)} onClick={remove} className="px-3 py-1.5 rounded-full text-xs text-stone-500 hover:text-[#E11D48]"><Trash2 size={14} /></button>}
          <button data-testid={T.bundleSave(id)} className="px-4 py-1.5 rounded-full text-xs font-medium bg-[#1C1917] text-white hover:bg-[#E06D44] transition-colors">{id === "new" ? "Create bundle" : "Save"}</button>
        </div>
      </div>
    </form>
  );
};

export const AdminBundles = ({ products }) => {
  const [bundles, setBundles] = useState(null);
  useEffect(() => { api.get("/admin/bundles").then(({ data }) => setBundles(data)).catch(() => setBundles([])); }, []);
  const sorted = [...products].sort((a, b) => a.name.localeCompare(b.name));
  if (!bundles) return <p className="mt-6 text-stone-500">Loading bundles…</p>;
  return (
    <section data-testid={T.bundlesList} className="mt-6 space-y-3">
      <p className="text-sm text-stone-600">Bundles show on the Kiddee page. When a cart has every product in a bundle, the bundle discount applies automatically. It never stacks with a discount code; the bigger saving wins.</p>
      {bundles.map((b) => (
        <BundleForm key={b.slug} initial={b} products={sorted}
          onSaved={(d) => setBundles((l) => l.map((x) => (x.slug === d.slug ? d : x)))}
          onDeleted={(slug) => setBundles((l) => l.filter((x) => x.slug !== slug))} />
      ))}
      <p data-testid="admin-bundle-new-heading" className="pt-4 font-heading font-bold">New bundle</p>
      <BundleForm initial={blank} products={sorted} onSaved={(d) => setBundles((l) => [...l, d])} onDeleted={() => {}} />
    </section>
  );
};
